import type {
  ValidationRequest,
  ObjectData,
} from 'discriminator';
import type {
  RoomLayout,
  Questionnaire,
  PythonExecutorOutput,
  Room,
  SceneData
} from './types.ts';
import { mapSceneDataToRoomLayout, parseSceneData } from './db-mapper.ts';
function parseToRgbObject(color: any): { r: number; g: number; b: number } | null {
  if (!color) return null;
  if (typeof color === 'object' && typeof color.r === 'number' && typeof color.g === 'number' && typeof color.b === 'number') {
    return { r: color.r, g: color.g, b: color.b };
  }
  if (typeof color === 'string') {
    const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1], 10),
        g: parseInt(rgbMatch[2], 10),
        b: parseInt(rgbMatch[3], 10)
      };
    }
    const hexMatch = color.match(/#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i);
    if (hexMatch) {
      return {
        r: parseInt(hexMatch[1], 16),
        g: parseInt(hexMatch[2], 16),
        b: parseInt(hexMatch[3], 16)
      };
    }
  }
  return null;
}

/**
 * Transforms internal/Python coordinate format (RoomLayout) into the Discriminator request payload.
 */
export function mapRoomLayoutToDiscriminatorRequest(
  roomDb: RoomLayout,
  questionnaire: Questionnaire
): ValidationRequest {
  const width = roomDb.width;
  const length = roomDb.length;
  const height = roomDb.height;

  const objects: ObjectData[] = roomDb.items.map((item) => {
    const meta = item.metadata || {};

    // Infer seating capacity
    let seatingCapacity = meta.seatingCapacity ?? 0;
    if (seatingCapacity === 0) {
      const typeLower = item.type.toLowerCase();
      const labelLower = item.label.toLowerCase();
      if (typeLower.includes('sofa') || labelLower.includes('sofa')) {
        seatingCapacity = 3;
      } else if (typeLower.includes('chair') || labelLower.includes('chair') || labelLower.includes('zidle') || labelLower.includes('kreslo')) {
        seatingCapacity = 1;
      }
    }

    const rawColors = item.colors || meta.colors || [];
    const normalizedColors = rawColors
      .map((c: any) => parseToRgbObject(c))
      .filter((c: any): c is { r: number; g: number; b: number } => c !== null);

    return {
      id: item.itemId,
      name: item.label,
      categoryId: meta.categoryId || meta.accesoryId || item.type || 'unknown',
      position: {
        x: (item.position.x - width / 2) * 1000,
        y: item.position.z * 1000,
        z: (item.position.y - length / 2) * 1000,
      },
      size: {
        x: item.size.width * 1000,
        y: item.size.height * 1000,
        z: item.size.length * 1000,
      },
      rotation: item.angle * Math.PI / 180,
      colors: normalizedColors,
      designStyle: meta.designStyle || [],
      material: item.materials || meta.material || [],
      seatingCapacity,
      isDominant: meta.isDominant || false,
      zoneId: (item as any).zoneId || meta.zoneId || null,
      isZoneDev: meta.isZoneDev || false,
      isBorder: meta.isBorder || false,
    };
  });

  const activeZones = roomDb.metadata?.zones && Array.isArray(roomDb.metadata.zones) && roomDb.metadata.zones.length > 0
    ? roomDb.metadata.zones.map((z: any) => ({
        id: z.id,
        polygon: z.polygon
      }))
    : null;

  return {
    lang: 'en',
    roomData: {
      size: {
        x: width * 1000,
        y: height * 1000,
        z: length * 1000,
      },
      objects,
    },
    questionnaire: {
      numberOfAdults: questionnaire.numberOfAdults,
      numberOfChildren: questionnaire.numberOfChildren,
      numberOfGuests: questionnaire.numberOfGuests,
      workplace: questionnaire.workplace,
      playground: questionnaire.playground,
    },
    zones: activeZones,
    paths: null,
  };
}

/**
 * Transforms external DB SceneData directly to the Discriminator request, optionally applying placements first.
 */
export function mapSceneDataToDiscriminatorRequest(
  roomOrScene: Room | SceneData,
  questionnaire: Questionnaire,
  placements?: PythonExecutorOutput['placements'],
  accessoryToCategoryMap?: Map<string, { categoryId: string; categoryName: string }>
): ValidationRequest {
  const sceneData = parseSceneData(roomOrScene);
  let roomDb = mapSceneDataToRoomLayout(sceneData, accessoryToCategoryMap);

  if (placements && placements.length > 0) {
    const placementMap = new Map(placements.map(p => [p.id, p]));
    roomDb = {
      ...roomDb,
      items: roomDb.items.map((item) => {
        const placed = placementMap.get(item.itemId);
        if (placed) {
          return {
            ...item,
            position: {
              x: placed.position.x,
              y: placed.position.y,
              z: placed.position.z,
            },
            angle: placed.rotation,
          };
        }
        return item;
      })
    };
  }

  return mapRoomLayoutToDiscriminatorRequest(roomDb, questionnaire);
}

/**
 * Legacy API for server integration compatibility.
 */
export function mapToDiscriminatorRequest(
  roomDb: RoomLayout,
  questionnaire: Questionnaire,
  placements: PythonExecutorOutput['placements']
): ValidationRequest {
  const placementMap = new Map(placements.map(p => [p.id, p]));
  const updatedRoomDb = {
    ...roomDb,
    items: roomDb.items.map((item) => {
      const placed = placementMap.get(item.itemId);
      if (placed) {
        return {
          ...item,
          position: {
            x: placed.position.x,
            y: placed.position.y,
            z: placed.position.z,
          },
          angle: placed.rotation,
        };
      }
      return item;
    })
  };
  return mapRoomLayoutToDiscriminatorRequest(updatedRoomDb, questionnaire);
}
